import {Template} from "./Template";
import * as React from 'react'
import * as ReactDOM from 'react-dom'

export interface TemplateableComponentProps {
	parent?:any
	template:Template
	placeholders?:StringMap<string>
}

/**
 * This component takes a Template and a set of placeholders.
 * The template is evaluated, resulting in a string that contains HTML and then added to the DOM using dangerouslySetInnerHTML.
 * Subclasses are able to override placeholdersToReactify() and return an object literal like so:
 *
 *  {
 *      Placeholder1 : <FirstComponentToUse myProp={blah} />,
 *      Placeholder2 : <OtherComponent yourProp={sneh} />
 *  }
 *
 * Each of the keys in that object literal will be used.
 */
export abstract class TemplateableComponent<P extends TemplateableComponentProps, S> extends React.Component<P, S> {

	protected getStaticPlaceholders():PlaceholderCollection {
		return {}
	}

	private getPlaceholders():PlaceholderCollection {

		const placeholders:PlaceholderCollection = {};

		Object.assign(placeholders, this.props.placeholders, this.getStaticPlaceholders());

		const toReactify = this.renderPlaceholders();
		const reactifiedPlaceholderNames:string[] = Object.keys(toReactify);
		for (let i = 0; i < reactifiedPlaceholderNames.length; i ++) {
			const placeholder = reactifiedPlaceholderNames[i];
			if (toReactify[placeholder]) {
				placeholders[placeholder] = `<span class="reactified-${placeholder}" />`;
			}
		}

		return placeholders;
	}

	render() {
		const evaluatedTemplate = this.props.template.evaluate(this.getPlaceholders());
		return <span dangerouslySetInnerHTML={{__html: evaluatedTemplate}} />;
	}

	/**
	 * This method returns an object literal, whereby the key is the name of a placeholder and the value is what
	 * you would normally return from a render() method. For example:
	 *
	 *  return {
	 *      Placeholder1 : <FirstComponentToUse myProp={blah} />,
	 *      Placeholder2 : <OtherComponent yourProp={sneh} />
	 *  }
	 */
	protected abstract renderPlaceholders():StringMap<React.ReactElement<any>|null>;

	componentDidMount() {
		this.renderPlaceholdersIntoTemplate();
	}

	componentDidUpdate() {
		this.renderPlaceholdersIntoTemplate();
	}

	private renderPlaceholdersIntoTemplate() {
		const node = ReactDOM.findDOMNode(this);
		const placeholders = this.renderPlaceholders();
		const placeholderNames = Object.keys(placeholders);
		for (let i = 0; i < placeholderNames.length; i ++) {
			const placeholder = placeholderNames[i];
			if (placeholders.hasOwnProperty(placeholder)) {
				const nodesToAttachTo = node.getElementsByClassName(`reactified-${placeholder}`);
				if (nodesToAttachTo.length > 1) {
					throw new Error(`Found ${nodesToAttachTo.length} nodes for ${placeholder} placeholder, but expected 0 or 1.`);
				} else if (nodesToAttachTo.length == 1) {
					const placeholder2 = placeholders[placeholder]
					if (placeholder2 != null) {
						ReactDOM.render( placeholder2, nodesToAttachTo[0] );
					}
				}
			}
		}

		this.placeholdersDidRender(node)
	}

	/**
	 * If you need to attach event listeners to rendered placeholders, this is the place to do it.
	 */
	protected placeholdersDidRender(node:Element):void {}
}

/**
 * Simple class to evaluate a template given a set of placeholders.
 */
export class TemplateComponent extends TemplateableComponent<TemplateableComponentProps, {}> {
	protected renderPlaceholders():StringMap<React.ReactElement<any>> { return {} }
}



// WEBPACK FOOTER //
// ./src/innertemplatemanager/TemplateableComponent.tsx